<template>
    <article class="order-widget mb-2" :class="status.cardClass">
        <button
        type="button"
        class="order-widget__summary w-100 border-0 bg-transparent text-start px-3 py-2"
        :aria-expanded="expanded ? 'true' : 'false'"
        :aria-controls="widgetId"
        :title="expanded ? $t('order_widget.hide_details') : $t('order_widget.show_details')"
        @click="expanded = !expanded"
        >
            <div class="d-flex align-items-start justify-content-between gap-2">
                <div class="flex-grow-1 min-w-0">
                    <div class="d-flex flex-wrap align-items-baseline gap-2">
                        <p class="order-widget__product-count fw-bolder text-gray-900 mb-0">{{ productCountText }}</p>
                        <span v-if="orderReference" class="order-widget__reference small text-gray-500">#{{ orderReference }}</span>
                    </div>
                </div>
                <div class="d-flex align-items-center gap-2 ms-auto flex-shrink-0">
                    <span class="order-widget__status" :class="status.pillClass">
                        <BootstrapIcon class="me-1 align-middle" :color="status.iconColor" name="circle-fill" :size="10" />
                        <span class="align-middle">{{ $t(status.labelKey) }}</span>
                    </span>
                    <BootstrapIcon class="order-widget__chevron" :name="expanded ? 'chevron-up' : 'chevron-down'" :size="14" />
                </div>
            </div>

            <div v-if="collapsedDetails.length > 0" class="order-widget__meta mt-2">
                <span v-for="detail in collapsedDetails" :key="detail.key" class="order-widget__meta-item">
                    <BootstrapIcon :name="detail.icon" :size="14" />
                    <span>{{ detail.value }}</span>
                </span>
            </div>

            <div v-if="searchCustomerDetails.length > 0" class="order-widget__search-result mt-3">
                <span v-for="detail in searchCustomerDetails" :key="detail.key" class="me-3">
                    <span>{{ $t(detail.labelKey) }}:</span>
                    <strong class="ms-1">{{ detail.value }}</strong>
                </span>
            </div>

        </button>

        <div v-if="expanded" :id="widgetId" class="order-widget__details border-top p-3">
            <div v-if="discardReason" class="order-widget__discard rounded p-3 mb-3">
                <strong class="me-2">{{ $t('requisition.discard_reason') }}:</strong>
                <span>{{ discardReason }}</span>
            </div>

            <div class="row g-3">
                <div v-if="safeItems.length > 0" class="col-12 col-lg-6">
                    <h3 class="h6 fw-bolder text-gray-800 mb-2">{{ $t('order_widget.products') }}</h3>
                    <div class="table-responsive rounded border">
                        <table class="table table-light small text-gray-700 align-middle mb-0">
                            <tbody v-for="(item, itemIndex) in safeItems" :key="itemKey(item, itemIndex)">
                                <tr>
                                    <td>{{ itemName(item) }}</td>
                                    <td class="text-end text-nowrap">{{ itemQuantity(item) }}{{ itemUnit(item) }}</td>
                                </tr>
                                <tr v-if="itemFieldSets(item).length > 0">
                                    <td colspan="2" class="pt-0">
                                        <div
                                        v-for="(fieldSet, setIndex) in itemFieldSets(item)"
                                        :key="setIndex"
                                        class="order-widget__field-set rounded px-2 py-1 mt-1"
                                        >
                                            <span v-for="(field, fieldIndex) in fieldSet" :key="fieldIndex" class="d-inline-block me-3">
                                                <strong v-if="fieldName(field)" class="me-1">{{ fieldName(field) }}:</strong>
                                                <span>{{ fieldValue(field) }}</span>
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="customerDetails.length > 0 || orderDetails.length > 0" class="col-12 col-lg-6">
                    <div v-if="customerDetails.length > 0" class="mb-3">
                        <h3 class="h6 fw-bolder text-gray-800 mb-2">{{ $t('order_widget.customer') }}</h3>
                        <div class="table-responsive rounded border">
                            <table class="table table-light small text-gray-700 mb-0">
                                <tbody>
                                    <tr v-for="detail in customerDetails" :key="detail.key">
                                        <th class="text-nowrap fw-normal">{{ $t(detail.labelKey) }}</th>
                                        <td>{{ detail.value }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div v-if="orderDetails.length > 0">
                        <h3 class="h6 fw-bolder text-gray-800 mb-2">{{ $t('order_widget.order_details') }}</h3>
                        <div class="table-responsive rounded border">
                            <table class="table table-light small text-gray-700 mb-0">
                                <tbody>
                                    <tr v-for="detail in orderDetails" :key="detail.key">
                                        <th class="text-nowrap fw-normal">{{ detail.label }}</th>
                                        <td>{{ detail.value }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <p v-if="safeItems.length === 0 && customerDetails.length === 0 && orderDetails.length === 0" class="text-gray-500 mb-0">
                {{ $t('order_widget.no_details') }}
            </p>

            <div v-if="hasActions" class="d-flex gap-2 flex-wrap mt-3">
                <button v-if="canPrint" type="button" class="btn btn-teal-500 bg-gradient text-white" @click="printReceipt">
                    <BootstrapIcon color="fill-white text-white" class="me-2" name="printer" :size="18" />
                    <span class="align-middle">{{ $t('requisition.print_receipt') }}</span>
                </button>
                <button v-if="canMarkDelivered" type="button" class="btn btn-brown-500 bg-gradient" :disabled="loading" @click="markDelivered">
                    <BootstrapIcon color="fill-gray-100 text-gray-100" class="me-2" name="box-seam" :size="18" />
                    <span class="align-middle">{{ $t('delivery.handed_to_customer') }}</span>
                </button>
                <button v-if="canEmailReceipt" type="button" class="btn btn-outline-secondary bg-gradient" @click="requestConfirmEmailReceipt">
                    <BootstrapIcon color="fill-gray-700 text-gray-700" class="me-2" name="envelope" :size="18" />
                    <span class="align-middle">{{ $t('requisition.send_receipt_on_email') }}</span>
                </button>
                <router-link v-if="canEdit" :to="editRoute" class="btn btn-primary bg-gradient">
                    <BootstrapIcon color="fill-gray-100 text-gray-100" class="me-2" name="pencil-square" :size="18" />
                    <span class="align-middle">{{ $t('requisition.edit_order') }}</span>
                </router-link>
                <button v-if="canDiscard" type="button" class="btn btn-orange-300 bg-gradient" @click="requestConfirmDiscardOrder">
                    <BootstrapIcon color="fill-gray-900 text-gray-900" class="me-2" name="file-earmark-minus" :size="18" />
                    <span class="align-middle">{{ $t('delivery.discard') }}</span>
                </button>
            </div>
        </div>
    </article>
</template>

<script>
import { createApp, defineComponent } from 'vue'
import DateFormat from 'date-fns/format'
import { enUS, ro } from 'date-fns/locale'
import BootstrapIcon from '@/common/directive/BootstrapIcon.vue'
import InventoryOrderStore from '@/common/store/inventory_order.js'
import TemplatePrintA8 from '@/modules/store/template/print_a8.vue'

const STATUS = Object.freeze({
    order_pending: {
        labelKey: 'delivery.pending',
        cardClass: 'order-widget--pending',
        pillClass: 'order-widget__status--pending',
        iconColor: 'fill-coral-500 text-coral-500'
    },
    order_preparing: {
        labelKey: 'delivery.preparing',
        cardClass: 'order-widget--preparing',
        pillClass: 'order-widget__status--preparing',
        iconColor: 'fill-teal-500 text-teal-500'
    },
    order_completed: {
        labelKey: 'delivery.completed',
        cardClass: 'order-widget--completed',
        pillClass: 'order-widget__status--completed',
        iconColor: 'fill-cyan-500 text-cyan-500'
    },
    order_discarded: {
        labelKey: 'delivery.discarded',
        cardClass: 'order-widget--discarded',
        pillClass: 'order-widget__status--discarded',
        iconColor: 'fill-orange-300 text-orange-300'
    },
    order_delivered: {
        labelKey: 'delivery.handed',
        cardClass: 'order-widget--delivered',
        pillClass: 'order-widget__status--delivered',
        iconColor: 'fill-brown-500 text-brown-500'
    }
})

const UNKNOWN_STATUS = Object.freeze({
    labelKey: 'order_widget.unknown_status',
    cardClass: 'order-widget--unknown',
    pillClass: 'order-widget__status--unknown',
    iconColor: 'fill-gray-500 text-gray-500'
})

export default {
    name: 'OrderWidget',
    components: {
        BootstrapIcon
    },
    props: {
        filters_search: {
            type: String,
            default: ''
        },
        orderIndex: {
            type: [String, Number],
            default: ''
        },
        order: {
            type: Object,
            default: () => ({})
        },
        business_details: {
            type: Object,
            default: () => ({})
        }
    },
    data: function () {
        return {
            expanded: false,
            loading: false
        }
    },
    computed: {
        locale: function () {
            return this.$i18n && typeof this.$i18n.locale === 'string' ? this.$i18n.locale : 'en'
        },
        widgetId: function () {
            const suffix = String(this.orderIndex || this.orderReference || 'order').replace(/[^a-zA-Z0-9_-]/g, '-')
            return `order-widget-${suffix}`
        },
        safeOrder: function () {
            return this.order && typeof this.order === 'object' && !Array.isArray(this.order) ? this.order : {}
        },
        safeItems: function () {
            return Array.isArray(this.safeOrder.items) ? this.safeOrder.items.filter(item => item && typeof item === 'object') : []
        },
        safeCustomer: function () {
            return this.safeOrder.customer && typeof this.safeOrder.customer === 'object' && !Array.isArray(this.safeOrder.customer)
                ? this.safeOrder.customer
                : {}
        },
        safeOrderData: function () {
            return this.safeOrder.data && typeof this.safeOrder.data === 'object' && !Array.isArray(this.safeOrder.data)
                ? this.safeOrder.data
                : {}
        },
        status: function () {
            return typeof this.safeOrder.status === 'string' && STATUS[this.safeOrder.status]
                ? STATUS[this.safeOrder.status]
                : UNKNOWN_STATUS
        },
        orderReference: function () {
            if (!this.hasText(this.safeOrder.id)) return ''
            return this.safeOrder.id.split('-')[0].toUpperCase()
        },
        productCountText: function () {
            const key = this.safeItems.length === 1 ? 'order_widget.product_count_one' : 'order_widget.product_count_many'
            return this.$t(key, { COUNT: this.safeItems.length })
        },
        customerName: function () {
            return this.hasText(this.safeCustomer.name) ? this.safeCustomer.name.trim() : ''
        },
        collapsedDetails: function () {
            const details = []
            const pickup = this.pickupDateTime
            const requester = this.memberName(this.safeOrder.requisition)
            const delivery = this.memberName(this.safeOrder.delivery)
            const created = this.formatDate(this.safeOrder.created_at)

            if (this.customerName) details.push({ key: 'customer', icon: 'person', value: this.customerName })
            if (pickup) details.push({ key: 'pickup', icon: 'box-seam', value: pickup })
            if (requester) details.push({ key: 'requester', icon: 'cart-plus', value: requester })
            if (delivery) details.push({ key: 'delivery', icon: 'cart-check', value: delivery })
            if (created) details.push({ key: 'created', icon: 'clock-history', value: created })
            return details
        },
        pickupDateTime: function () {
            const date = this.hasText(this.safeOrderData.pickup_date) ? this.safeOrderData.pickup_date.trim() : ''
            const hour = this.hasText(this.safeOrderData.pickup_hour) ? this.safeOrderData.pickup_hour.trim() : ''
            return [this.formatPickupDate(date), hour].filter(Boolean).join(' ')
        },
        customerDetails: function () {
            return [
                { key: 'name', labelKey: 'requisition.name', value: this.safeCustomer.name },
                { key: 'phone', labelKey: 'requisition.phone', value: this.safeCustomer.phone },
                { key: 'email', labelKey: 'requisition.email', value: this.safeCustomer.email },
                { key: 'address', labelKey: 'requisition.address', value: this.safeCustomer.address }
            ].filter(detail => this.hasDisplayValue(detail.value))
        },
        searchCustomerDetails: function () {
            return this.hasText(this.filters_search) ? this.customerDetails.slice(0, 3) : []
        },
        orderDetails: function () {
            return Object.entries(this.safeOrderData)
                .filter(([, value]) => this.hasDisplayValue(value))
                .map(([key, value]) => ({
                    key,
                    label: this.translationOrKey(`requisition.${key}`, key),
                    value: this.displayValue(value)
                }))
        },
        discardReason: function () {
            return this.safeOrder.status === 'order_discarded' && this.hasText(this.safeOrder.reason) ? this.safeOrder.reason.trim() : ''
        },
        canPrint: function () {
            return Boolean(this.orderReference)
        },
        canMarkDelivered: function () {
            return Boolean(this.orderReference && this.safeOrder.status === 'order_completed' && this.hasText(this.safeOrder.customer_id))
        },
        canEmailReceipt: function () {
            return Boolean(this.orderReference && this.hasText(this.safeCustomer.email))
        },
        canEdit: function () {
            return Boolean(this.orderReference && this.safeOrder.status === 'order_pending')
        },
        canDiscard: function () {
            return this.canEdit
        },
        hasActions: function () {
            return this.canPrint || this.canMarkDelivered || this.canEmailReceipt || this.canEdit || this.canDiscard
        },
        editRoute: function () {
            return this.hasText(this.safeOrder.id) ? `/requisition?order_id=${encodeURIComponent(this.safeOrder.id)}` : '/requisition'
        },
        printableOrder: function () {
            return {
                ...this.safeOrder,
                id: this.hasText(this.safeOrder.id) ? this.safeOrder.id : '',
                location: this.safeOrder.location && typeof this.safeOrder.location === 'object' ? this.safeOrder.location : {},
                requisition: this.safeOrder.requisition && typeof this.safeOrder.requisition === 'object' ? this.safeOrder.requisition : {},
                customer: this.safeCustomer,
                data: this.safeOrderData,
                items: this.safeItems
            }
        }
    },
    methods: {
        hasText: function (value) {
            return typeof value === 'string' && value.trim().length > 0
        },
        hasDisplayValue: function (value) {
            if (this.hasText(value)) return true
            if (typeof value === 'number' || typeof value === 'boolean') return true
            return Array.isArray(value) ? value.length > 0 : Boolean(value && typeof value === 'object' && Object.keys(value).length > 0)
        },
        displayValue: function (value) {
            if (Array.isArray(value)) return value.map(this.displayValue).join(', ')
            if (value && typeof value === 'object') {
                try {
                    return JSON.stringify(value)
                } catch (error) {
                    return ''
                }
            }
            return value === null || typeof value === 'undefined' ? '' : String(value)
        },
        translationOrKey: function (translationKey, fallbackKey) {
            if (typeof this.$te === 'function' && this.$te(translationKey)) return this.$t(translationKey)
            return String(fallbackKey).replace(/_/g, ' ')
        },
        memberName: function (member) {
            return member && typeof member === 'object' && this.hasText(member.username) ? member.username.trim() : ''
        },
        formatDate: function (timestamp) {
            if (!timestamp) return ''
            const date = new Date(timestamp)
            if (Number.isNaN(date.getTime())) return ''
            return DateFormat(date, 'dd MMM yyyy', { locale: this.locale === 'ro' ? ro : enUS })
        },
        formatPickupDate: function (value) {
            if (!this.hasText(value)) return ''
            const rawDate = value.trim()
            const parts = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/)
            if (!parts) return rawDate

            const year = Number(parts[1])
            const month = Number(parts[2])
            const day = Number(parts[3])
            const date = new Date(year, month - 1, day)
            const isValid = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
            if (!isValid) return rawDate

            return DateFormat(date, 'dd MMM yyyy', { locale: this.locale === 'ro' ? ro : enUS })
        },
        itemKey: function (item, index) {
            return this.hasText(item.id) ? item.id : index
        },
        itemName: function (item) {
            return this.hasText(item.name) ? item.name.trim() : this.$t('order_widget.unnamed_product')
        },
        itemQuantity: function (item) {
            const quantity = item._pivot_quantity
            return typeof quantity === 'number' || this.hasText(quantity) ? quantity : this.$t('order_widget.quantity_unavailable')
        },
        itemUnit: function (item) {
            if (typeof item.unit !== 'number' && !this.hasText(item.unit)) return ''
            const key = `units.unit_abbr_${item.unit}`
            return typeof this.$te === 'function' && this.$te(key) ? ` ${this.$t(key)}` : ''
        },
        itemFieldSets: function (item) {
            const fields = item._pivot_data && typeof item._pivot_data === 'object' ? item._pivot_data.fields : null
            if (!Array.isArray(fields)) return []
            return fields
                .filter(fieldSet => Array.isArray(fieldSet))
                .map(fieldSet => fieldSet.filter(field => field && typeof field === 'object' && this.hasDisplayValue(field.value)))
                .filter(fieldSet => fieldSet.length > 0)
        },
        fieldName: function (field) {
            return this.hasText(field.name) ? field.name.trim() : ''
        },
        fieldValue: function (field) {
            return this.displayValue(field.value)
        },
        markDelivered: async function () {
            if (!this.canMarkDelivered || this.loading) return
            this.loading = true
            try {
                const response = await InventoryOrderStore.dispatch('UPDATE_DELIVERY', {
                    id: this.safeOrder.id,
                    params: { status: 'order_delivered' }
                })
                if (response && response.status === 0) {
                    this.$bus.$emit('Notification/show', {
                        title: this.$t('requisition.operation_successful'),
                        msg: this.$t('order_widget.changes_saved'),
                        level: 'success'
                    })
                    this.$bus.$emit('Order/status_handed')
                } else {
                    this.showUpdateError()
                }
            } catch (error) {
                this.showUpdateError()
            } finally {
                this.loading = false
            }
        },
        showUpdateError: function () {
            this.$bus.$emit('Notification/show', {
                title: this.$t('requisition.something_went_wrong'),
                msg: this.$t('requisition.please_retry_the_operation_or_refresh'),
                level: 'danger'
            })
        },
        printReceipt: function () {
            if (!this.canPrint) return
            const printWindow = window.open('', '', 'height=500, width=500')
            if (!printWindow) return
            const self = this
            const component = defineComponent({
                extends: TemplatePrintA8,
                data: function () {
                    return {
                        type: 'requisition',
                        order: self.printableOrder,
                        created_at: self.formatDate(self.safeOrder.created_at),
                        business_details: self.business_details && typeof self.business_details === 'object'
                            ? { ...self.business_details }
                            : {}
                    }
                },
                methods: {
                    $t: self.$t
                }
            })
            const container = printWindow.document.createElement('div')
            const instance = createApp(component).mount(container)
            printWindow.document.write(`<html><body>${instance.$el.outerHTML}</body></html>`)
            printWindow.document.close()
            printWindow.print()
        },
        requestConfirmEmailReceipt: function () {
            if (this.canEmailReceipt) this.$bus.$emit('Order/email_receipt', this.safeOrder)
        },
        requestConfirmDiscardOrder: function () {
            if (this.canDiscard) this.$bus.$emit('Order/discard_order', this.safeOrder)
        }
    }
}
</script>
